import { create } from 'zustand';
import { buildDecisionDeck } from './decisions';

/* =========================================
   Political Offices — Brazilian hierarchy
========================================= */

export const POLITICAL_OFFICES = [
  { id: 'assessor',      name: 'Assessor',       tier: 0, emoji: '📎', esfera: 'Município', poder: 'Legislativo', desc: 'Assessor de gabinete de vereador',                    salary: 5,  turns: 8,  stakeMultiplier: 1.0, mandato: 4, idadeMinima: 18, sistema: null,           turnos: 0, mapLevel: 'municipio', req: null },
  { id: 'vereador',      name: 'Vereador',       tier: 1, emoji: '🏛️', esfera: 'Município', poder: 'Legislativo', desc: 'Cria leis municipais e fiscaliza o prefeito',         salary: 12, turns: 10, stakeMultiplier: 1.3, mandato: 4, idadeMinima: 18, sistema: 'proporcional', turnos: 1, mapLevel: 'municipio', req: { popularity: 20, influence: 15, money: 50 } },
  { id: 'prefeito',      name: 'Prefeito',       tier: 2, emoji: '🏘️', esfera: 'Município', poder: 'Executivo',   desc: 'Chefe do Executivo municipal',                       salary: 25, turns: 10, stakeMultiplier: 1.6, mandato: 4, idadeMinima: 21, sistema: 'majoritario', turnos: 1, mapLevel: 'municipio', req: { popularity: 30, influence: 25, money: 100, controlReq: 'camara' } },
  { id: 'dep_estadual',  name: 'Dep. Estadual',  tier: 3, emoji: '📜', esfera: 'Estado',    poder: 'Legislativo', desc: 'Cria leis estaduais na Assembleia Legislativa',       salary: 35, turns: 12, stakeMultiplier: 2.0, mandato: 4, idadeMinima: 21, sistema: 'proporcional', turnos: 1, mapLevel: 'estado',    req: { popularity: 40, influence: 30, money: 150, controlReq: 'municipios_3' } },
  { id: 'dep_federal',   name: 'Dep. Federal',   tier: 4, emoji: '🏛️', esfera: 'União',     poder: 'Legislativo', desc: 'Cria leis federais na Câmara dos Deputados',          salary: 45, turns: 12, stakeMultiplier: 2.5, mandato: 4, idadeMinima: 21, sistema: 'proporcional', turnos: 1, mapLevel: 'pais',      req: { popularity: 50, influence: 40, money: 200, controlReq: 'municipios_5' } },
  { id: 'governador',    name: 'Governador',     tier: 5, emoji: '⭐', esfera: 'Estado',    poder: 'Executivo',   desc: 'Chefe do Executivo estadual',                        salary: 55, turns: 14, stakeMultiplier: 3.0, mandato: 4, idadeMinima: 30, sistema: 'majoritario', turnos: 2, mapLevel: 'estado',    req: { popularity: 55, influence: 50, money: 300, controlReq: 'prefeituras_majority' } },
  { id: 'senador',       name: 'Senador',        tier: 6, emoji: '🎖️', esfera: 'União',     poder: 'Legislativo', desc: 'Revisa leis e julga autoridades no Senado Federal',   salary: 60, turns: 14, stakeMultiplier: 3.5, mandato: 8, idadeMinima: 35, sistema: 'majoritario', turnos: 1, mapLevel: 'pais',      req: { popularity: 65, influence: 55, money: 400, controlReq: 'estados_3' } },
  { id: 'presidente',    name: 'Presidente',     tier: 7, emoji: '🇧🇷', esfera: 'União',     poder: 'Executivo',   desc: 'Presidente da República',                            salary: 80, turns: 16, stakeMultiplier: 4.0, mandato: 4, idadeMinima: 35, sistema: 'majoritario', turnos: 2, mapLevel: 'pais',      req: { popularity: 75, influence: 70, money: 500, controlReq: 'estados_majority' } },
];

export function getOffice(id) {
  return POLITICAL_OFFICES.find(o => o.id === id) || POLITICAL_OFFICES[0];
}

/* =========================================
   Urban Problems & Allocation System
========================================= */

export const PROBLEM_TYPES = {
  transporte:  { id: 'transporte',  label: 'Transporte',  emoji: '🚌', color: '#3b82f6' },
  seguranca:   { id: 'seguranca',   label: 'Segurança',   emoji: '🛡️', color: '#ef4444' },
  saude:       { id: 'saude',       label: 'Saúde',       emoji: '🏥', color: '#10b981' },
  educacao:    { id: 'educacao',    label: 'Educação',    emoji: '📚', color: '#8b5cf6' },
  saneamento:  { id: 'saneamento',  label: 'Saneamento',  emoji: '💧', color: '#06b6d4' },
  eventos:     { id: 'eventos',     label: 'Eventos',     emoji: '🎉', color: '#f59e0b' },
};

export const ALLOCATION_IMPACTS = {
  transporte:  { vereadorCarisma: 3, assessorCarisma: 1, influence: 2, money: -15, risk: 2, desc: 'Melhorar ônibus e vias — visibilidade do vereador' },
  seguranca:   { vereadorCarisma: 4, assessorCarisma: 2, influence: 3, money: -20, risk: 5, desc: 'Ronda e iluminação — alto risco de denúncia' },
  saude:       { vereadorCarisma: 5, assessorCarisma: 3, influence: 2, money: -25, risk: 1, desc: 'Postos de saúde — grande vitrine política' },
  educacao:    { vereadorCarisma: 3, assessorCarisma: 4, influence: 4, money: -20, risk: 1, desc: 'Escolas e creches — forma rede de contatos' },
  saneamento:  { vereadorCarisma: 2, assessorCarisma: 1, influence: 1, money: -10, risk: 0, desc: 'Água e esgoto — barato mas pouco visível' },
  eventos:     { vereadorCarisma: 1, assessorCarisma: 5, influence: 3, money: -15, risk: 3, desc: 'Festas e ações sociais — fama do assessor' },
};

function generateProblems() {
  const types = Object.keys(PROBLEM_TYPES);
  const numProblems = 2 + Math.floor(Math.random() * 3);
  const shuffled = types.sort(() => Math.random() - 0.5);
  const problems = {};
  for (let i = 0; i < numProblems; i++) {
    problems[shuffled[i]] = { severity: 1 + Math.floor(Math.random() * 5), invested: 0 };
  }
  return problems;
}

/* =========================================
   Territory helpers
========================================= */

export function createTerritory(id, name, population, neighbors = [], problems = null) {
  return {
    id,
    name,
    population,
    controlledBy: null,
    influence: {},
    allocatedResources: { cabos: 0, money: 0, rallies: 0 },
    problems: problems || generateProblems(),
    neighbors,
  };
}

export function resolveTerritory(territory, playerVereadorId, vereadores, allTerritories) {
  const inf = { ...territory.influence };
  const r = territory.allocatedResources;

  // Player investment from classic resources → goes to player's vereador
  if (playerVereadorId) {
    inf[playerVereadorId] = (inf[playerVereadorId] || 0) + r.cabos * 5 + r.money * 0.1 + r.rallies * 20;
  }

  // Player investment from problem-solving
  const probs = { ...(territory.problems || {}) };
  let problemInfluence = 0;
  for (const [, prob] of Object.entries(probs)) {
    if (prob.invested > 0) {
      problemInfluence += prob.invested * 3;
      prob.severity = Math.max(0, prob.severity - Math.ceil(prob.invested * 0.5));
    }
  }
  if (playerVereadorId) {
    inf[playerVereadorId] = (inf[playerVereadorId] || 0) + problemInfluence;
  }

  // Spillover from neighbors (15%)
  for (const nId of territory.neighbors || []) {
    const neighbor = allTerritories[nId];
    if (!neighbor) continue;
    for (const [verId, val] of Object.entries(neighbor.influence || {})) {
      if (val > 5) inf[verId] = (inf[verId] || 0) + val * 0.12;
    }
  }

  // Decay for all
  for (const key of Object.keys(inf)) {
    inf[key] = Math.max(0, (inf[key] || 0) * 0.92);
  }

  // AI vereadores invest each turn
  for (const ver of vereadores) {
    if (ver.id === playerVereadorId) continue;
    const isHome = territory.controlledBy === ver.id;
    const playerInf = inf[playerVereadorId] || 0;
    const isContested = playerInf > 10 && (inf[ver.id] || 0) > 5;
    const isNeutral = !territory.controlledBy;

    let investment = 0;
    if (isHome) {
      investment = ver.strength * (3 + Math.random() * 3);
    } else if (isContested) {
      investment = ver.strength * (2 + Math.random() * 4);
    } else if (isNeutral && Math.random() < 0.3) {
      investment = ver.strength * (1 + Math.random() * 2);
    } else if (Math.random() < 0.1) {
      investment = ver.strength * Math.random() * 2;
    }

    if (investment > 0) inf[ver.id] = (inf[ver.id] || 0) + investment;
  }

  // Problems can worsen if not addressed
  for (const [key, prob] of Object.entries(probs)) {
    if (prob.invested === 0 && prob.severity > 0 && Math.random() < 0.25) {
      probs[key] = { ...prob, severity: Math.min(5, prob.severity + 1) };
    }
    probs[key] = { ...probs[key], invested: 0 };
  }

  // Determine control: vereador with highest influence wins
  let maxId = null, maxInf = 0;
  for (const [verId, val] of Object.entries(inf)) {
    if (val > maxInf) { maxId = verId; maxInf = val; }
  }

  return {
    ...territory,
    influence: inf,
    controlledBy: maxInf > 15 ? maxId : null,
    allocatedResources: { cabos: 0, money: 0, rallies: 0 },
    problems: probs,
  };
}

/* =========================================
   Coalition helpers
========================================= */

export function calculateCoalitionCost(partySize) {
  return { influence: 10 + partySize * 2, money: 20 + partySize * 5 };
}

/* =========================================
   Win condition checks
========================================= */

export function checkWinCondition(officeId, territories, playerVereadorId, coalitionVereadorIds = []) {
  const office = getOffice(officeId);
  if (!office.req?.controlReq) return true;
  const cr = office.req.controlReq;

  const alliedIds = [playerVereadorId, ...coalitionVereadorIds];
  const total = Object.keys(territories).length;
  const controlled = Object.values(territories).filter(t => alliedIds.includes(t.controlledBy)).length;

  if (cr === 'camara') return controlled > total / 2;
  if (cr === 'prefeituras_majority') return controlled > total / 2;
  if (cr === 'estados_majority') return controlled > total / 2;
  if (cr.startsWith('municipios_')) { const n = parseInt(cr.split('_')[1]); return controlled >= n; }
  if (cr.startsWith('estados_')) { const n = parseInt(cr.split('_')[1]); return controlled >= n; }
  return controlled > 0;
}

/* =========================================
   Ficha Limpa
========================================= */

export function checkFichaLimpa(player) {
  if (player.risk >= 80) return { eligible: false, reason: 'Risco ≥80%. Lei da Ficha Limpa impede candidatura.' };
  return { eligible: true };
}

export function getAvailablePromotions(player, currentOfficeId, territories, playerIdOrParty, coalitionIdsOrParties) {
  const current = getOffice(currentOfficeId);
  const fl = checkFichaLimpa(player);
  if (!fl.eligible) return { promotions: [], fichaLimpaBlocked: true, reason: fl.reason };

  const promotions = POLITICAL_OFFICES.filter(o => {
    if (o.tier <= current.tier) return false;
    if (o.tier > current.tier + 2) return false;
    if (!o.req) return true;
    const meetsStats = player.popularity >= o.req.popularity && player.influence >= o.req.influence && player.money >= o.req.money;
    const meetsControl = !o.req.controlReq || checkWinCondition(o.id, territories, playerIdOrParty, coalitionIdsOrParties);
    return meetsStats && meetsControl;
  });

  return { promotions, fichaLimpaBlocked: false };
}

/* =========================================
   Initial state
========================================= */

/* =========================================
   Election Simulation
========================================= */

export function simulateElection(officeId, player, cityData, partyInfluence) {
  const office = getOffice(officeId);
  if (office.sistema === 'proporcional') return simulateProporcional(officeId, player, cityData, partyInfluence);
  return simulateMajoritario(officeId, player, cityData, partyInfluence);
}

function simulateProporcional(officeId, player, cityData, partyInfluence) {
  const pop = cityData?.populacao || 100000;
  const totalEleitores = Math.round(pop * 0.75);
  const vagas = cityData?.totalVereadores || 15;
  const numPartidos = (cityData?.partidos?.length || 8);
  const votosValidos = Math.round(totalEleitores * (0.7 + Math.random() * 0.15));
  const quocienteEleitoral = Math.round(votosValidos / vagas);

  const partyVoteShare = (partyInfluence / 100) * 0.6 + (player.popularity / 100) * 0.3 + Math.random() * 0.1;
  const playerPartyVotes = Math.round(votosValidos * Math.min(0.35, partyVoteShare / numPartidos * 3));
  const playerSeats = Math.max(0, Math.floor(playerPartyVotes / quocienteEleitoral));

  const playerVotes = Math.round(playerPartyVotes * (0.3 + (player.popularity / 200) + Math.random() * 0.15));
  const otherCandidates = Math.max(1, Math.floor(3 + Math.random() * 4));
  const playerRank = playerVotes > playerPartyVotes * 0.25 ? 1 : Math.min(otherCandidates, Math.ceil(Math.random() * 3) + 1);
  const elected = playerSeats > 0 && playerRank <= playerSeats;

  return {
    type: 'proporcional', elected, totalEleitores, votosValidos, vagas,
    quocienteEleitoral, playerPartyVotes, playerSeats, playerVotes, playerRank,
    details: elected
      ? `Parabéns! Com ${playerVotes.toLocaleString('pt-BR')} votos, você foi o ${playerRank}º mais votado do partido e conquistou uma das ${playerSeats} vagas.`
      : `Com ${playerVotes.toLocaleString('pt-BR')} votos, você ficou em ${playerRank}º no partido. O partido conquistou ${playerSeats} vaga(s) — insuficiente.`,
  };
}

function simulateMajoritario(officeId, player, cityData, partyInfluence) {
  const office = getOffice(officeId);
  const pop = cityData?.populacao || 100000;
  const totalEleitores = Math.round(pop * 0.75);
  const votosValidos = Math.round(totalEleitores * (0.7 + Math.random() * 0.15));

  const baseShare = 0.15 + (player.popularity / 100) * 0.35 + (partyInfluence / 100) * 0.15 + (player.influence / 100) * 0.1;
  const playerPct = Math.min(0.65, baseShare + (Math.random() * 0.1 - 0.05));
  const playerVotes = Math.round(votosValidos * playerPct);

  const opponents = [];
  let remaining = 1 - playerPct;
  const numOpp = 2 + Math.floor(Math.random() * 3);
  for (let i = 0; i < numOpp; i++) {
    const share = i === 0 ? remaining * (0.4 + Math.random() * 0.3) : remaining * Math.random();
    remaining -= share;
    const oppParty = (cityData?.partidos || ['OPOSIÇÃO'])[Math.floor(Math.random() * (cityData?.partidos?.length || 1))];
    opponents.push({ name: oppParty, pct: share, votes: Math.round(votosValidos * share) });
  }
  opponents.sort((a, b) => b.pct - a.pct);

  const needsTwoRounds = (office.turnos === 2) || (officeId === 'prefeito' && totalEleitores > 200000);
  let elected = playerPct > 0.5;
  let secondRound = null;

  if (needsTwoRounds && !elected && playerPct > opponents[0]?.pct) {
    const bonus = (player.influence / 100) * 0.1 + Math.random() * 0.08;
    const r2Player = playerPct + bonus;
    const r2Opp = 1 - r2Player;
    elected = r2Player > 0.5;
    secondRound = { playerPct: r2Player, opponentPct: r2Opp, opponent: opponents[0]?.name };
  }

  return {
    type: 'majoritario', elected, totalEleitores, votosValidos, playerPct, playerVotes,
    opponents, secondRound, needsTwoRounds,
    details: elected
      ? `Vitória! Você conquistou ${(playerPct * 100).toFixed(1)}% dos votos válidos${secondRound ? ' no segundo turno' : ''}.`
      : `Derrota. Com ${(playerPct * 100).toFixed(1)}% dos votos, não foi suficiente para vencer.`,
  };
}

/* =========================================
   Initial state
========================================= */

const INITIAL_PLAYER = { popularity: 5, influence: 0, money: 0, risk: 5, vereadorCarisma: 10 };
const MAX_TURNS = 8;

/* =========================================
   Store
========================================= */

const useGameStore = create((set, get) => ({
  /* ── Player stats ── */
  player: { ...INITIAL_PLAYER },

  /* ── Game progress ── */
  turn: 0,
  maxTurns: MAX_TURNS,
  phase: 'idle',       // idle | playing | gameover
  turnPhase: 'event',  // event | allocate | resolve
  outcome: null,

  /* ── Political career ── */
  currentOffice: 'assessor',
  round: 1,
  careerPath: ['assessor'],
  playerParty: null,

  /* ── Territory ── */
  mapLevel: 'municipio', // municipio | estado | pais
  territories: {},
  rivalParties: [],
  vereadores: [],         // all vereadores with game data
  playerVereadorId: null,  // which vereador the player works for

  /* ── Coalitions ── */
  coalitions: [],
  coalitionParties: [],

  /* ── Resources (for map allocation) ── */
  resourcePool: { cabos: 3, money: 50, rallies: 1 },

  /* ── City / metadata ── */
  cityData: null,
  newsDecisions: [],
  lastDecision: null,
  lastTerritoryChanges: [],
  electionResult: null,
  partyInfluence: 30,

  /* ── React-driven turn flow ── */
  decisionDeck: [],
  currentDecision: null,
  turnState: 'decision', // decision | map | turnSummary | mandate_end | election | gameover
  objectives: [],

  /* ═══════════════════
     ACTIONS
  ═══════════════════ */

  startGame: (cityData, avatarBase, vereadorBonus) => {
    const party = cityData?.vereadorPartido || 'JOGADOR';
    const partidos = cityData?.partidos || [];
    const rivals = partidos.filter(p => p !== party).slice(0, 6).map(p => ({ id: p, strength: 0.3 + Math.random() * 0.4 }));

    // Build vereadores list from cityData.gameVereadores (passed from index.js)
    const rawVereadores = cityData?.gameVereadores || [];
    const totalVotos = Math.max(1, rawVereadores.reduce((s, v) => s + (v.votos || 0), 0));
    const vereadores = rawVereadores.map((v, i) => ({
      id: `ver-${i}`,
      nome: v.nome || `Vereador ${i + 1}`,
      partido: v.partido || 'SEM PARTIDO',
      foto: v.foto || null,
      sqCandidato: v.sqCandidato || null,
      votos: v.votos || 0,
      strength: 0.3 + ((v.votos || 0) / totalVotos) * 1.5,
      isPlayerBoss: v.sqCandidato === cityData?.vereadorSqCandidato,
    }));
    const playerVereadorId = vereadores.find(v => v.isPlayerBoss)?.id || vereadores[0]?.id || null;

    set({
      player: {
        popularity: (avatarBase?.carisma ?? INITIAL_PLAYER.popularity) + (vereadorBonus?.carisma || 0),
        influence:  (avatarBase?.influencia ?? INITIAL_PLAYER.influence) + (vereadorBonus?.influencia || 0),
        money:      (avatarBase?.recursos ?? INITIAL_PLAYER.money) + (vereadorBonus?.recursos || 0),
        risk:       (avatarBase?.risco ?? INITIAL_PLAYER.risk) + (vereadorBonus?.risco || 0),
        vereadorCarisma: 10 + (vereadorBonus?.carisma || 0),
      },
      cityData,
      turn: 0,
      maxTurns: MAX_TURNS,
      phase: 'playing',
      turnPhase: 'event',
      outcome: null,
      currentOffice: 'assessor',
      round: 1,
      careerPath: ['assessor'],
      playerParty: party,
      mapLevel: 'municipio',
      territories: {},
      rivalParties: rivals,
      vereadores,
      playerVereadorId,
      coalitions: [],
      coalitionParties: [],
      resourcePool: { cabos: 3, money: 50, rallies: 1 },
      electionResult: null,
      partyInfluence: 30,
    });
  },

  /** Initialize territories from zone GeoJSON */
  initTerritories: (zonesGeoJSON) => {
    // Rival vereadores here (not rivalParties) — territories at this level
    // feed the same municipio-level resolveTerritory() as initBairrosTerritories,
    // which keys influence/controlledBy by vereador id (ver-N), not party name.
    const { vereadores, playerVereadorId } = get();
    const rivalVereadores = vereadores.filter(v => v.id !== playerVereadorId);
    const territories = {};
    const features = zonesGeoJSON?.features || [];

    features.forEach((f, i) => {
      const id = `zona-${f.properties?.zonaId ?? i}`;
      const neighbors = features
        .filter((_, j) => j !== i && Math.abs(j - i) <= 2)
        .map((nf, ni) => `zona-${nf.properties?.zonaId ?? ni}`);

      const t = createTerritory(id, f.properties?.nome || `Zona ${i}`, f.properties?.populacao || 10000, neighbors);

      // Some zones start controlled by a rival vereador
      if (i > 0 && Math.random() < 0.5 && rivalVereadores.length > 0) {
        const rival = rivalVereadores[Math.floor(Math.random() * rivalVereadores.length)];
        t.controlledBy = rival.id;
        t.influence[rival.id] = 15 + Math.random() * 20;
      }
      territories[id] = t;
    });

    set({ territories });
  },

  /** Initialize territories from state municipalities GeoJSON */
  initStateTerritories: (stateGeoJSON) => {
    const { rivalParties, playerParty, cityData } = get();
    const territories = {};
    const features = stateGeoJSON?.features || [];

    features.forEach((f, i) => {
      const munId = `mun-${f.properties?.id || i}`;
      const neighbors = [];
      territories[munId] = createTerritory(munId, f.properties?.name || `Município ${i}`, 50000, neighbors);

      // Player's home city starts with some influence
      if (String(f.properties?.id) === String(cityData?.codigoIbge)) {
        territories[munId].controlledBy = playerParty;
        territories[munId].influence[playerParty] = 40;
      } else if (Math.random() < 0.3 && rivalParties.length > 0) {
        const rival = rivalParties[Math.floor(Math.random() * rivalParties.length)];
        territories[munId].controlledBy = rival.id;
        territories[munId].influence[rival.id] = 15 + Math.random() * 15;
      }
    });

    set({ territories, mapLevel: 'estado' });
  },

  /** Initialize territories from Brazil states GeoJSON */
  initCountryTerritories: (brazilGeoJSON) => {
    const { rivalParties, playerParty, cityData } = get();
    const territories = {};
    const features = brazilGeoJSON?.features || [];

    features.forEach((f) => {
      const sigla = f.properties?.sigla || f.properties?.UF_05 || '';
      const stateId = `estado-${sigla}`;
      territories[stateId] = createTerritory(stateId, sigla, 5000000, []);

      if (sigla === cityData?.stateSigla) {
        territories[stateId].controlledBy = playerParty;
        territories[stateId].influence[playerParty] = 50;
      } else if (Math.random() < 0.4 && rivalParties.length > 0) {
        const rival = rivalParties[Math.floor(Math.random() * rivalParties.length)];
        territories[stateId].controlledBy = rival.id;
        territories[stateId].influence[rival.id] = 20 + Math.random() * 20;
      }
    });

    set({ territories, mapLevel: 'pais' });
  },

  /** Apply decision effects — Phase 1: Event */
  applyAction: (effects) => {
    const { player, turn, currentOffice } = get();
    const m = getOffice(currentOffice).stakeMultiplier;
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    // Generate new resources based on effects
    const cabosGain = Math.max(0, Math.round(((effects.influence || 0) + (effects.pop || 0)) * 0.1));
    const moneyGain = Math.max(0, effects.money || 0);
    const ralliesGain = (effects.pop || 0) > 10 ? 1 : 0;

    const { resourcePool } = get();

    set({
      player: {
        popularity: clamp(player.popularity + Math.round((effects.pop || 0) * m), 0, 100),
        influence:  clamp(player.influence + Math.round((effects.influence || 0) * m), 0, 100),
        money:      player.money + Math.round((effects.money || 0) * m),
        risk:       clamp(player.risk + Math.round((effects.risk || 0) * m), 0, 100),
      },
      turn: turn + 1,
      turnPhase: 'allocate',
      resourcePool: {
        cabos: resourcePool.cabos + cabosGain,
        money: resourcePool.money + moneyGain,
        rallies: resourcePool.rallies + ralliesGain,
      },
    });
  },

  /** Allocate resource to a territory — Phase 2: Allocate */
  allocateResource: (territoryId, resourceType, amount) => {
    const { territories, resourcePool } = get();
    const t = territories[territoryId];
    if (!t || !resourcePool[resourceType] || resourcePool[resourceType] < amount) return;

    const newTerritories = { ...territories };
    newTerritories[territoryId] = {
      ...t,
      allocatedResources: { ...t.allocatedResources, [resourceType]: (t.allocatedResources[resourceType] || 0) + amount },
    };

    set({
      territories: newTerritories,
      resourcePool: { ...resourcePool, [resourceType]: resourcePool[resourceType] - amount },
    });
  },

  /** Allocate effort to solve a problem in a territory */
  allocateProblem: (territoryId, problemType) => {
    const { territories, player } = get();
    const t = territories[territoryId];
    if (!t || !t.problems?.[problemType]) return;

    const impact = ALLOCATION_IMPACTS[problemType];
    if (!impact) return;

    const cost = Math.abs(impact.money);
    if (player.money < cost) return;

    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    const newProblems = { ...t.problems };
    newProblems[problemType] = {
      ...newProblems[problemType],
      invested: (newProblems[problemType].invested || 0) + 1,
    };

    const newTerritories = { ...territories };
    newTerritories[territoryId] = { ...t, problems: newProblems };

    set({
      territories: newTerritories,
      player: {
        ...player,
        popularity: clamp(player.popularity + impact.assessorCarisma, 0, 100),
        influence: clamp(player.influence + impact.influence, 0, 100),
        money: player.money + impact.money,
        risk: clamp(player.risk + impact.risk, 0, 100),
        vereadorCarisma: clamp((player.vereadorCarisma || 10) + impact.vereadorCarisma, 0, 100),
      },
      lastDecision: {
        pop: impact.assessorCarisma,
        influence: impact.influence,
        money: impact.money,
        risk: impact.risk,
        vereadorCarisma: impact.vereadorCarisma,
        problemType,
      },
    });
  },

  /** Resolve all territories — Phase 3: Resolve */
  resolveTerritories: () => {
    const { territories, playerVereadorId, vereadores } = get();
    const newTerritories = {};
    const changes = [];

    for (const [id, t] of Object.entries(territories)) {
      const before = t.controlledBy;
      const resolved = resolveTerritory(t, playerVereadorId, vereadores, territories);
      newTerritories[id] = resolved;
      if (resolved.controlledBy !== before) {
        changes.push({
          territoryId: id,
          territoryName: t.name,
          fromId: before,
          toId: resolved.controlledBy,
        });
      }
    }

    set({ territories: newTerritories, turnPhase: 'event', lastTerritoryChanges: changes });
  },

  /** Propose coalition with a party */
  proposeCoalition: (partyId) => {
    const { player, coalitions, coalitionParties } = get();
    if (coalitionParties.includes(partyId)) return false;

    const cost = calculateCoalitionCost(coalitionParties.length + 1);
    if (player.influence < cost.influence || player.money < cost.money) return false;

    const success = Math.random() < 0.6 + (player.influence / 200);
    if (!success) return false;

    set({
      player: { ...player, influence: player.influence - cost.influence, money: player.money - cost.money },
      coalitions: [...coalitions, { partyId, since: get().turn, strength: 0.5 + Math.random() * 0.3 }],
      coalitionParties: [...coalitionParties, partyId],
    });
    return true;
  },

  /** Break a coalition */
  breakCoalition: (partyId) => {
    const { coalitions, coalitionParties, territories } = get();
    const newTerritories = { ...territories };

    // Territories controlled by the broken coalition party revert
    for (const [id, t] of Object.entries(newTerritories)) {
      if (t.controlledBy === partyId) {
        newTerritories[id] = { ...t, controlledBy: null };
      }
    }

    set({
      coalitions: coalitions.filter(c => c.partyId !== partyId),
      coalitionParties: coalitionParties.filter(p => p !== partyId),
      territories: newTerritories,
    });
  },

  /** Promote to a new office */
  promoteToOffice: (officeId) => {
    const { player, careerPath, partyInfluence } = get();
    const office = getOffice(officeId);
    const riskReduction = Math.min(20, player.risk * 0.3);
    const partyBoost = Math.min(15, office.tier * 3);

    set({
      currentOffice: officeId,
      round: careerPath.length + 1,
      careerPath: [...careerPath, officeId],
      turn: 0,
      maxTurns: office.turns,
      phase: 'playing',
      turnPhase: 'event',
      outcome: null,
      electionResult: null,
      mapLevel: office.mapLevel,
      territories: {},
      coalitions: [],
      coalitionParties: [],
      partyInfluence: Math.min(80, partyInfluence + partyBoost),
      resourcePool: { cabos: 3 + office.tier, money: 50 + office.salary, rallies: 1 + Math.floor(office.tier / 2) },
      player: {
        popularity: Math.max(10, Math.round(player.popularity * 0.7)),
        influence:  Math.max(5,  Math.round(player.influence * 0.7)),
        money:      player.money + office.salary,
        risk:       Math.max(0, Math.round(player.risk - riskReduction)),
        vereadorCarisma: player.vereadorCarisma || 10,
      },
    });
  },

  setOutcome: (outcome) => set({ outcome, phase: 'gameover', turnState: 'gameover' }),
  setNewsDecisions: (decisions) => set({ newsDecisions: decisions }),
  setTurnPhase: (turnPhase) => set({ turnPhase }),

  /* ═══════════════════
     REACT TURN FLOW
  ═══════════════════ */

  initDecisionDeck: () => {
    const { cityData, newsDecisions, maxTurns } = get();
    const deck = buildDecisionDeck(cityData, newsDecisions, maxTurns);
    set({ decisionDeck: deck, currentDecision: deck[0] || null, turnState: 'decision' });
  },

  nextDecision: () => {
    const { turn, maxTurns, player, decisionDeck } = get();
    if (player.risk >= 100) { get().setOutcome('exposed'); return; }
    if (player.popularity <= 0) { get().setOutcome('irrelevant'); return; }
    if (turn >= maxTurns) { set({ turnState: 'mandate_end' }); return; }
    const next = decisionDeck[turn] || null;
    if (!next) { set({ turnState: 'mandate_end' }); return; }
    set({ currentDecision: next, turnState: 'decision' });
  },

  chooseOption: (optionIndex) => {
    const { currentDecision } = get();
    if (!currentDecision) return;
    const opt = currentDecision.options[optionIndex];
    if (!opt) return;
    get().applyAction({ pop: opt.pop || 0, influence: opt.influence || 0, money: opt.money || 0, risk: opt.risk || 0 });
    set({ lastDecision: { pop: opt.pop || 0, influence: opt.influence || 0, money: opt.money || 0, risk: opt.risk || 0 }, turnState: 'map' });
  },

  endMapPhase: () => {
    get().resolveTerritories();
    get().checkObjectives();
    const { player } = get();
    if (player.risk >= 100) { get().setOutcome('exposed'); return; }
    if (player.popularity <= 0) { get().setOutcome('irrelevant'); return; }
    set({ turnState: 'turnSummary' });
  },

  /** Dismiss the end-of-round territory summary and move on to the next decision */
  dismissTurnSummary: () => {
    get().nextDecision();
  },

  runForElection: (officeId) => {
    const { player, cityData, partyInfluence } = get();
    const office = getOffice(officeId);
    const cost = { money: Math.round((office.req?.money || 50) * 0.3), risk: 5 + office.tier * 3 };
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    set({
      player: { ...player, money: player.money - cost.money, risk: clamp(player.risk + cost.risk, 0, 100) },
    });

    const result = simulateElection(officeId, get().player, cityData, partyInfluence);
    set({ electionResult: result, turnState: 'election' });
    return result;
  },

  handleElectionResult: (elected, officeId) => {
    if (elected) {
      get().promoteToOffice(officeId);
      const { currentOffice } = get();
      if (currentOffice === 'presidente') { get().setOutcome('presidente'); return; }
      set({ turnState: 'decision' });
      get().initDecisionDeck();
      get().generateObjectives();
    } else {
      const { player } = get();
      const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
      set({
        player: { ...player, popularity: clamp(player.popularity + 5, 0, 100), influence: clamp(player.influence + 3, 0, 100) },
        turnState: 'decision',
      });
      get().promoteToOffice(get().currentOffice);
      get().initDecisionDeck();
      get().generateObjectives();
    }
  },

  stayInOffice: () => {
    get().promoteToOffice(get().currentOffice);
    set({ turnState: 'decision' });
    get().initDecisionDeck();
    get().generateObjectives();
  },

  generateObjectives: () => {
    const { currentOffice } = get();
    const office = getOffice(currentOffice);
    const tier = office.tier;

    const objectives = [
      { id: 'territory', text: `Controle ${3 + tier * 2} territórios`, type: 'territory', target: 3 + tier * 2, current: 0, completed: false },
      { id: 'popularity', text: `Atinja ${15 + tier * 10}% de carisma`, type: 'stat', stat: 'popularity', target: 15 + tier * 10, current: 0, completed: false },
      { id: 'influence', text: `Atinja ${10 + tier * 8}% de influência`, type: 'stat', stat: 'influence', target: 10 + tier * 8, current: 0, completed: false },
      { id: 'survive', text: 'Termine sem escândalo (risco < 80)', type: 'risk', target: 80, current: 0, completed: false },
    ];

    if (tier >= 1) {
      objectives.push({ id: 'coalition', text: 'Forme coligação com 1 partido', type: 'coalition', target: 1, current: 0, completed: false });
    }

    set({ objectives });
  },

  checkObjectives: () => {
    const { objectives, player, territories, playerVereadorId, coalitionParties, vereadores } = get();
    if (!objectives.length) return;

    const coalitionVereadorIds = vereadores.filter(v => coalitionParties.includes(v.partido)).map(v => v.id);
    const alliedIds = [playerVereadorId, ...coalitionVereadorIds];
    const controlled = Object.values(territories).filter(t => alliedIds.includes(t.controlledBy)).length;

    const updated = objectives.map(obj => {
      let current = 0;
      let completed = false;

      switch (obj.type) {
        case 'territory': current = controlled; completed = current >= obj.target; break;
        case 'stat': current = player[obj.stat] || 0; completed = current >= obj.target; break;
        case 'risk': current = player.risk; completed = current < obj.target; break;
        case 'coalition': current = coalitionParties.length; completed = current >= obj.target; break;
        default: break;
      }

      return { ...obj, current, completed };
    });

    set({ objectives: updated });
  },

  /** Initialize territories from bairros GeoJSON — distribute among real vereadores */
  initBairrosTerritories: (bairrosGeoJSON) => {
    const { vereadores, playerVereadorId } = get();
    const territories = {};
    const features = bairrosGeoJSON?.features || [];

    // Distribute bairros proportionally among vereadores by vote share
    const totalVotos = Math.max(1, vereadores.reduce((s, v) => s + v.votos, 0));
    const numBairros = features.length;

    // Build assignment: each vereador gets bairros proportional to their vote share
    const assignments = [];
    let assignedCount = 0;
    const sortedVereadores = [...vereadores].sort((a, b) => b.votos - a.votos);

    for (let vi = 0; vi < sortedVereadores.length; vi++) {
      const v = sortedVereadores[vi];
      const share = v.votos / totalVotos;
      let count = Math.max(1, Math.round(numBairros * share));
      if (vi === sortedVereadores.length - 1) count = numBairros - assignedCount;
      else count = Math.min(count, numBairros - assignedCount - (sortedVereadores.length - vi - 1));
      if (count < 1) count = 1;
      for (let j = 0; j < count && assignedCount < numBairros; j++) {
        assignments.push(v.id);
        assignedCount++;
      }
    }
    // Fill remaining with the player's vereador
    while (assignments.length < numBairros) {
      assignments.push(playerVereadorId || (vereadores[0]?.id));
    }

    features.forEach((f, i) => {
      const id = `bairro-${f.properties?.id || f.properties?.osm_id || i}`;
      const neighbors = features
        .filter((_, j) => j !== i && Math.abs(j - i) <= 2)
        .map((nf, ni) => `bairro-${nf.properties?.id || nf.properties?.osm_id || ni}`);

      const problems = generateProblems();
      const t = createTerritory(id, f.properties?.nome || `Bairro ${i}`, f.properties?.populacao || 5000, neighbors, problems);

      const assignedVer = assignments[i];
      if (assignedVer) {
        t.controlledBy = assignedVer;
        const ver = vereadores.find(v => v.id === assignedVer);
        t.influence[assignedVer] = 20 + (ver?.strength || 0.5) * 15;
      }

      territories[id] = t;
    });

    set({ territories });
  },

  /** Get territory stats */
  getTerritoryStats: () => {
    const { territories, playerVereadorId } = get();
    const all = Object.values(territories);
    const controlled = all.filter(t => t.controlledBy === playerVereadorId);
    const rival = all.filter(t => t.controlledBy && t.controlledBy !== playerVereadorId);
    const neutral = all.filter(t => !t.controlledBy);
    return { total: all.length, controlled: controlled.length, rival: rival.length, neutral: neutral.length };
  },

  reset: () => set({
    player: { ...INITIAL_PLAYER, vereadorCarisma: 10 },
    cityData: null,
    newsDecisions: [],
    lastDecision: null,
    lastTerritoryChanges: [],
    turn: 0,
    maxTurns: MAX_TURNS,
    phase: 'idle',
    turnPhase: 'event',
    outcome: null,
    currentOffice: 'assessor',
    round: 1,
    careerPath: ['assessor'],
    playerParty: null,
    mapLevel: 'municipio',
    territories: {},
    rivalParties: [],
    vereadores: [],
    playerVereadorId: null,
    coalitions: [],
    coalitionParties: [],
    resourcePool: { cabos: 3, money: 50, rallies: 1 },
    electionResult: null,
    partyInfluence: 30,
    decisionDeck: [],
    currentDecision: null,
    turnState: 'decision',
    objectives: [],
  }),
}));

export default useGameStore;
export const gameStore = useGameStore;
